import React, { useState } from "react";
import { useTranslation } from "react-i18next";
import Customer from "../../../models/Customer";
import { useCustomer, useCustomers } from "../../../networking/customer.hooks";
import AutoCompleteInput, { AutoCompleteInputProps } from "./AutoCompleteInput";

interface Props
	extends Pick<
		AutoCompleteInputProps<string, Customer>,
		"width" | "icon" | "placeholder" | "prefix" | "autoFocus"
	> {
	onChange: (customerId?: string, customer?: Customer) => any;
	value?: string;
	pool?: string;
	filter?: (customer: Customer) => boolean;
	active?: boolean;
	hidePrefix?: boolean;
}

const CustomerAutoComplete: React.FC<Props> = ({
	pool,
	value,
	onChange,
	filter = () => true,
	active,
	hidePrefix,
	...rest
}) => {
	const [search, setSearch] = useState<string>();
	const { t } = useTranslation();

	const { customers } = useCustomers(
		{
			page: 1,
			pageSize: 20,
			sort: "name",
			pool,
			keyword: search,
			active: active,
		},
		!!search
	);

	const { customer } = useCustomer(value, !!value);

	function getSuggestions() {
		return (customers?.customers || []).filter(filter);
	}

	return (
		<AutoCompleteInput<string, Customer>
			value={value}
			getDisplayValue={(c) => (c ? `${c.name} (#${c.customerNumber})` : "")}
			// Members of a corporate group are named alike across the group
			// ("Gustav Vasa skola" vs "Vasaskolan"), so the group is what tells
			// two hits apart — show it after the name instead of only the number.
			renderSuggestion={(c) => (
				// 0.9375rem keeps the row at the size AppText (the default renderer) gave it
				<span className="flex flex-wrap items-baseline gap-x-1.5 text-[0.9375rem]">
					<span>{`${c.name} (#${c.customerNumber})`}</span>
					{c.corporateGroup?.name && (
						<span className="text-sm text-muted-foreground">– {c.corporateGroup.name}</span>
					)}
				</span>
			)}
			suggestions={getSuggestions()}
			getValue={(c) => c.id}
			onSearch={setSearch}
			minChars={1}
			prefix={hidePrefix ? undefined : t("CUSTOMER")}
			onChange={onChange}
			initialValueSuggestion={customer}
			{...rest}
		/>
	);
};

export default CustomerAutoComplete;
